import React, { useState } from "react";
import { Share2, X, MessageCircle, Facebook, Twitter, Send, Link2, Check } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

/** Floating multi-network share FAB on EVERY page (academy + whole site).
 *  Networks: WhatsApp, Facebook, X/Twitter, Telegram + copy-link (covers
 *  Instagram, which has no web share URL — users paste the copied link).
 *  Bottom-left so it never overlaps the clinic chat button (bottom-right). */
const ACADEMY_TITLES = {
  "/academy": "Qur'an & Arabic Academy",
  "/academy/teachers": "Tajweed Teachers",
  "/academy/quran": "Qur'an Reader",
  "/academy/arabic": "Learn Arabic",
  "/academy/tutors": "Live Qur'an & Arabic Tutors",
  "/academy/how-it-works": "How the Academy Works",
  "/academy/pronunciation": "AI Pronunciation Coach",
  "/academy/pass": "Academy Pass",
  "/academy/certificates": "Certificates & Ijāza",
};

const titleFor = (path) => {
  if (ACADEMY_TITLES[path]) return ACADEMY_TITLES[path];
  if (path.startsWith("/academy/quran/")) return "Qur'an Reader";
  if (path.startsWith("/academy/tutors/")) return "Tutor Profile";
  if (path.startsWith("/academy")) return "Qur'an & Arabic Academy";
  return "Islamic Cure & Care";
};

const LABELS = { en: "Share", ar: "مشاركة", ur: "شیئر کریں", de: "Teilen", fr: "Partager" };

const AcademyShareButton = () => {
  const { pathname } = useLocation();
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const title = titleFor(pathname);
  const text = `📖 ${title} — Islamic Cure & Care`;
  const enc = encodeURIComponent;
  const links = {
    whatsapp: `https://wa.me/?text=${enc(text + " · " + url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    twitter: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`,
    telegram: `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`,
  };
  const label = LABELS[lang] || LABELS.en;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { console.debug("copy failed:", e); }
  };

  const Net = ({ href, onClick, color, Icon, name, testid }) => (
    <a
      data-testid={testid}
      href={href} onClick={onClick}
      target={href ? "_blank" : undefined} rel="noreferrer"
      className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-cream text-sm text-ink transition"
    >
      <span className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: color }}>
        <Icon className="w-4 h-4" />
      </span>
      {name}
    </a>
  );

  return (
    <div className="fixed bottom-5 left-5 z-50" style={{ direction: "ltr" }}>
      {open && (
        <div data-testid="share-menu" className="mb-3 w-60 clay-surface rounded-2xl p-2 shadow-2xl shadow-black/20 bg-white animate-fade-in-up">
          <div className="flex items-center justify-between px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-widest text-gold font-semibold">{label}</span>
            <button data-testid="share-close" onClick={() => setOpen(false)} className="text-mute hover:text-forest"><X className="w-4 h-4" /></button>
          </div>
          <Net testid="share-net-whatsapp" href={links.whatsapp} color="#25D366" Icon={MessageCircle} name="WhatsApp" />
          <Net testid="share-net-facebook" href={links.facebook} color="#1877F2" Icon={Facebook} name="Facebook" />
          <Net testid="share-net-twitter" href={links.twitter} color="#000000" Icon={Twitter} name="X / Twitter" />
          <Net testid="share-net-telegram" href={links.telegram} color="#26A5E4" Icon={Send} name="Telegram" />
          <Net testid="share-net-copy" href={undefined} onClick={(e) => { e.preventDefault(); copy(); }} color="#B08D57" Icon={copied ? Check : Link2} name={copied ? "Copied! (paste on Instagram)" : "Copy link"} />
        </div>
      )}
      <button
        data-testid="share-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label={label} title={label}
        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-medium pl-4 pr-5 py-3 rounded-full shadow-2xl shadow-black/20 transition-all hover:-translate-y-0.5"
      >
        <Share2 className="w-5 h-5" />
        <span className="hidden sm:inline text-sm whitespace-nowrap">{label}</span>
      </button>
    </div>
  );
};

export default AcademyShareButton;
